import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where, updateDoc, doc, getDoc, addDoc, deleteDoc } from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../context/AuthContext";
import { Download, Plus, Search, Pencil, Trash2, Receipt, Wallet, CheckCircle2, Ban } from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_WHITE, ACTION_BTN_ICON } from "../styles/actionButtons";
import toast from "react-hot-toast";
import AddExpenseForm from "../components/AddExpenseForm";
import CaseStatCards from "../components/crm/cases/CaseStatCards";
import ExpenseAnalytics from "../components/crm/expenses/ExpenseAnalytics";
import DateRangeFilter from "../components/crm/expenses/DateRangeFilter";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";
import { syncExpenseToClient, reSyncExpenseToClient, deleteSyncedExpense } from "../services/expenseSyncService";

import SelectMenu from "../components/crm/SelectMenu";
function AllExpenses() {
  const { globalUser } = useAuth();
  const isClient = localStorage.getItem("userType") === "client";
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [userName, setUserName] = useState("");

  // Search / filters / pagination (light CRM table)
  const [searchQuery, setSearchQuery] = useState("");
  const [billableFilter, setBillableFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);

  useEffect(() => {
    if (isClient) {
      fetchClientSharedExpenses();
    } else {
      fetchUserName();
      fetchAllCases();
      fetchAllClients();
      fetchAllExpenses();
    }
  }, [globalUser]);

  const fetchUserName = async () => {
    if (!globalUser?.uid) return;

    try {
      const userDocRef = doc(db, "lawyers", globalUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log("Fetched user data:", userData);
        console.log("Setting userName to:", userData.name || "User");
        setUserName(userData.name || "User");
      } else {
        console.log("No user document found for UID:", globalUser.uid);
        setUserName("User");
      }
    } catch (error) {
      console.error("Error fetching user name:", error);
      setUserName("User");
    }
  };

  const fetchAllClients = async () => {
    if (!globalUser?.uid) return;
    try {
      const clientsQuery = query(
        collection(db, "clients"),
        where("owner", "==", globalUser.uid)
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsList = clientsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClients(clientsList);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchClientSharedExpenses = async () => {
    if (!globalUser?.uid) return;
    setLoading(true);
    try {
      const sharedExpensesRef = collection(db, "client", globalUser.uid, "sharedExpenses");
      const sharedExpensesSnapshot = await getDocs(sharedExpensesRef);

      const allExpenses = sharedExpensesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        isSharedExpense: true,
      }));

      // Sort by createdAt timestamp (newest first)
      allExpenses.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.date).getTime();
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.date).getTime();
        return timeB - timeA;
      });

      setExpenses(allExpenses);
      setFilteredExpenses(allExpenses);
    } catch (error) {
      console.error("Error fetching shared expenses:", error);
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, billableFilter, typeFilter, dateFrom, dateTo]);

  const fetchAllCases = async () => {
    if (!globalUser?.uid) return;

    try {
      const allCases = [];
      const collections = ["pending", "disposed"];

      for (const collectionName of collections) {
        const casesQuery = query(
          collection(db, collectionName),
          where("owner", "==", globalUser.uid)
        );
        const casesSnapshot = await getDocs(casesQuery);
        casesSnapshot.docs.forEach((doc) => {
          allCases.push({
            id: doc.id,
            collection: collectionName,
            caseNo: doc.data().caseNo,
            petitionerName: doc.data().petitionerName,
            respondentName: doc.data().respondentName,
            ...doc.data(),
          });
        });
      }

      setCases(allCases);
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Failed to load cases");
    }
  };

  const fetchAllExpenses = async () => {
    if (!globalUser?.uid) return;

    setLoading(true);
    try {
      const allExpenses = [];
      const collections = ["pending", "disposed"];

      // Fetch expenses from cases
      for (const collectionName of collections) {
        const casesQuery = query(
          collection(db, collectionName),
          where("owner", "==", globalUser.uid)
        );
        const casesSnapshot = await getDocs(casesQuery);

        casesSnapshot.docs.forEach((caseDoc) => {
          const caseData = caseDoc.data();
          if (caseData.expenses && Array.isArray(caseData.expenses)) {
            caseData.expenses.forEach((expense) => {
              allExpenses.push({
                ...expense,
                caseId: caseDoc.id,
                caseNo: caseData.caseNo,
                caseName: `${caseData.petitionerName} VS ${caseData.respondentName}`,
                collection: collectionName,
                createdBy: expense.userName || userName || "User",
              });
            });
          }
        });
      }

      // Fetch general expenses (not tied to any case)
      try {
        const generalExpensesRef = collection(db, "lawyers", globalUser.uid, "generalExpenses");
        const generalExpensesSnapshot = await getDocs(generalExpensesRef);

        generalExpensesSnapshot.docs.forEach((doc) => {
          const expenseData = doc.data();
          allExpenses.push({
            ...expenseData,
            id: doc.id,
            caseId: null,
            caseNo: null,
            caseName: "General Expense",
            collection: "generalExpenses",
            isGeneralExpense: true,
            createdBy: expenseData.userName || userName || "User",
          });
        });
      } catch (error) {
        console.error("Error fetching general expenses:", error);
      }

      // Sort by createdAt timestamp (newest first) - this ensures general expenses come on top if added recently
      allExpenses.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.date).getTime();
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.date).getTime();
        return timeB - timeA;
      });

      setExpenses(allExpenses);
      setFilteredExpenses(allExpenses);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (newExpense, selectedCaseId) => {
    try {
      // Ensure we have the latest userName
      let currentUserName = userName;
      if (!currentUserName || currentUserName === "User") {
        // Fetch userName again if not available
        const userDocRef = doc(db, "lawyers", globalUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          currentUserName = userDoc.data().name || "User";
          setUserName(currentUserName);
        }
      }

      console.log("Adding expense with userName:", currentUserName);

      if (selectedCaseId) {
        // Add expense to a specific case
        const selectedCase = cases.find((c) => c.id === selectedCaseId);
        if (!selectedCase) {
          toast.error("Case not found");
          return;
        }

        const caseDocRef = doc(db, selectedCase.collection, selectedCaseId);
        const caseSnapshot = await getDoc(caseDocRef);

        if (caseSnapshot.exists()) {
          const caseData = caseSnapshot.data();
          const currentExpenses = caseData.expenses || [];
          const expenseWithUser = {
            ...newExpense,
            userName: currentUserName,
          };
          const updatedExpenses = [...currentExpenses, expenseWithUser];

          await updateDoc(caseDocRef, {
            expenses: updatedExpenses,
          });

          // Sync to client if shareToClient is "yes"
          if (newExpense.shareToClient === "yes" && newExpense.contactId) {
            await syncExpenseToClient(
              newExpense.contactId,
              { ...expenseWithUser, caseNo: caseData.caseNo, caseName: `${caseData.petitionerName} VS ${caseData.respondentName}` },
              newExpense.id,
              globalUser.uid,
              currentUserName
            );
          }

          toast.success("Expense added successfully");
          fetchAllExpenses();
        }
      } else {
        // Add general expense (not tied to a case) to user's general expenses collection
        const generalExpenseData = {
          ...newExpense,
          userId: globalUser.uid,
          userName: currentUserName,
          createdAt: new Date().toISOString(),
        };

        const docRef = await addDoc(collection(db, "lawyers", globalUser.uid, "generalExpenses"), generalExpenseData);

        // Sync to client if shareToClient is "yes"
        if (newExpense.shareToClient === "yes" && newExpense.contactId) {
          await syncExpenseToClient(
            newExpense.contactId,
            { ...generalExpenseData, caseName: "General Expense" },
            docRef.id,
            globalUser.uid,
            currentUserName
          );
        }

        toast.success("General expense added successfully");
        fetchAllExpenses();
      }
    } catch (error) {
      console.error("Error adding expense:", error);
      toast.error("Failed to add expense");
    }
  };

  const handleEditExpense = async (updatedExpense) => {
    try {
      if (updatedExpense.isGeneralExpense) {
        // Update general expense
        const expenseDocRef = doc(db, "lawyers", globalUser.uid, "generalExpenses", updatedExpense.id);
        await updateDoc(expenseDocRef, {
          date: updatedExpense.date,
          type: updatedExpense.type,
          description: updatedExpense.description,
          amount: updatedExpense.amount,
          billable: updatedExpense.billable,
          attachments: updatedExpense.attachments,
          contactId: updatedExpense.contactId || "",
          contactName: updatedExpense.contactName || "",
          shareToClient: updatedExpense.shareToClient || "no",
          updatedAt: updatedExpense.updatedAt,
        });

        // Syncing removed - expenses are one-time share only
        // if (updatedExpense.shareToClient === "yes" && updatedExpense.contactId) {
        //   await reSyncExpenseToClient(...)
        // }

        toast.success("Expense updated successfully");
      } else {
        // Update case expense
        const caseDocRef = doc(db, updatedExpense.collection, updatedExpense.caseId);
        const caseSnapshot = await getDoc(caseDocRef);

        if (caseSnapshot.exists()) {
          const caseData = caseSnapshot.data();
          const updatedExpenses = caseData.expenses.map((expense) =>
            expense.id === updatedExpense.id ? updatedExpense : expense
          );

          await updateDoc(caseDocRef, {
            expenses: updatedExpenses,
          });

          // Syncing removed - expenses are one-time share only
          // if (updatedExpense.shareToClient === "yes" && updatedExpense.contactId) {
          //   await reSyncExpenseToClient(...)
          // }

          toast.success("Expense updated successfully");
        }
      }

      setEditMode(false);
      setExpenseToEdit(null);
      fetchAllExpenses();
    } catch (error) {
      console.error("Error updating expense:", error);
      toast.error("Failed to update expense");
    }
  };

  const handleDeleteExpense = async (expense) => {
    try {
      // Syncing removed - expenses are one-time share only
      // if (expense.shareToClient === "yes" && expense.contactId) {
      //   await deleteSyncedExpense(expense.contactId, expense.id);
      // }

      if (expense.isGeneralExpense) {
        // Delete general expense
        const expenseDocRef = doc(db, "lawyers", globalUser.uid, "generalExpenses", expense.id);
        await deleteDoc(expenseDocRef);

        toast.success("Expense deleted successfully");
      } else {
        // Delete case expense
        const caseDocRef = doc(db, expense.collection, expense.caseId);
        const caseSnapshot = await getDoc(caseDocRef);

        if (caseSnapshot.exists()) {
          const caseData = caseSnapshot.data();
          const updatedExpenses = caseData.expenses.filter((e) => e.id !== expense.id);

          await updateDoc(caseDocRef, {
            expenses: updatedExpenses,
          });

          toast.success("Expense deleted successfully");
        }
      }

      fetchAllExpenses();
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Failed to delete expense");
    }
  };

  const handleOpenEditModal = (expense) => {
    setExpenseToEdit(expense);
    setEditMode(true);
    setIsFormOpen(true);
  };

  const handleCloseModal = () => {
    setIsFormOpen(false);
    setEditMode(false);
    setExpenseToEdit(null);
  };

  const handleExportExpenses = () => {
    if (filteredExpenses.length === 0) {
      toast.error("No expenses to export");
      return;
    }

    try {
      const headers = ["Date", "Type", "Description", "Amount", "Billable", "Case Number", "Case Name"];
      const csvContent = [
        headers.join(","),
        ...filteredExpenses.map((expense) =>
          [
            expense.date,
            `"${expense.type}"`,
            `"${expense.description || ""}"`,
            expense.amount,
            expense.billable ? "Billable" : "Non-Billable",
            `"${expense.caseNo || ""}"`,
            `"${expense.caseName || ""}"`,
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `all_expenses_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Expenses exported successfully");
    } catch (error) {
      console.error("Error exporting expenses:", error);
      toast.error("Failed to export expenses");
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const fmtExpDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const chipSelectCls =
    "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-600 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  // Apply the selected date range to the base list; everything below (stat
  // cards, analytics, table) reads from this ranged set.
  const inDateRange = (e) => {
    if (!dateFrom && !dateTo) return true;
    const raw = e.date || (e.createdAt ? String(e.createdAt).slice(0, 10) : null);
    if (!raw) return false;
    const et = new Date(raw).getTime();
    if (isNaN(et)) return false;
    if (dateFrom && et < new Date(dateFrom).getTime()) return false;
    if (dateTo && et > new Date(dateTo + "T23:59:59").getTime()) return false;
    return true;
  };
  const rangedExpenses = filteredExpenses.filter(inDateRange);

  const totalRangedAmount = rangedExpenses.reduce(
    (s, e) => s + (parseFloat(e.amount) || 0),
    0
  );
  const billableAmount = rangedExpenses
    .filter((e) => e.billable)
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const nonBillableAmount = rangedExpenses
    .filter((e) => !e.billable)
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  // Pending reimbursements = billable expenses not yet marked paid.
  const pendingReimbursements = rangedExpenses
    .filter((e) => e.billable && e.paymentStatus !== "paid")
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const typeOptions = [
    ...new Set(expenses.map((e) => e.type).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));

  const visibleExpenses = rangedExpenses.filter((e) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (e.caseName || "").toLowerCase().includes(q) ||
      (e.contactName || "").toLowerCase().includes(q) ||
      (e.type || "").toLowerCase().includes(q) ||
      (e.description || "").toLowerCase().includes(q) ||
      (e.caseNo || "").toLowerCase().includes(q);
    const matchesBillable =
      billableFilter === "all" ||
      (billableFilter === "billable" ? e.billable : !e.billable);
    const matchesType = typeFilter === "all" || e.type === typeFilter;
    return matchesSearch && matchesBillable && matchesType;
  });
  const pageExpenses = visibleExpenses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex items-center justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              Expenses
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Track and manage case expenses
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DateRangeFilter
              from={dateFrom}
              to={dateTo}
              onChange={(f, t) => {
                setDateFrom(f);
                setDateTo(t);
              }}
            />
            {!isClient && (
              <>
                <button
                  onClick={handleExportExpenses}
                  className={ACTION_BTN_WHITE}
                >
                  <Download className={ACTION_BTN_ICON} />
                  Export
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setExpenseToEdit(null);
                    setIsFormOpen(true);
                  }}
                  className={ACTION_BTN_GOLD}
                >
                  <Plus className={ACTION_BTN_ICON} />
                  Add Expense
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stat cards (analytics) */}
        <CaseStatCards
          className="mt-4"
          cards={[
            { label: "Total Expenses", value: formatAmount(totalRangedAmount), Icon: Wallet, tint: "blue" },
            { label: "Billable Expenses", value: formatAmount(billableAmount), Icon: CheckCircle2, tint: "green" },
            { label: "Non-Billable Expenses", value: formatAmount(nonBillableAmount), Icon: Ban, tint: "amber" },
            { label: "Pending Reimbursements", value: formatAmount(pendingReimbursements), Icon: Receipt, tint: "purple" },
          ]}
        />

        {/* Analytics charts (above the table) */}
        <div className="mt-4">
          <ExpenseAnalytics expenses={rangedExpenses} formatAmount={formatAmount} />
        </div>

        {/* Filter row */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
            />
          </div>
          <SelectMenu value={billableFilter} onChange={(e) => setBillableFilter(e.target.value)} className={chipSelectCls} aria-label="Filter by billable">
            <option value="all">All</option>
            <option value="billable">Billable</option>
            <option value="nonbillable">Non-Billable</option>
          </SelectMenu>
          <SelectMenu value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={chipSelectCls} aria-label="Filter by type">
            <option value="all">Type</option>
            {typeOptions.map((t) => (<option key={t} value={t}>{t}</option>))}
          </SelectMenu>
          {(searchQuery || billableFilter !== "all" || typeFilter !== "all") && (
            <button
              type="button"
              onClick={() => { setSearchQuery(""); setBillableFilter("all"); setTypeFilter("all"); }}
              className="inline-flex items-center rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800"
            >
              Clear
            </button>
          )}
        </div>

        {/* Expenses table */}
        <div className="mt-4">
          {visibleExpenses.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <Receipt size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-lg">
                {expenses.length === 0 ? "No expenses yet." : "No expenses match your filters."}
              </p>
              {expenses.length === 0 && !isClient && (
                <p className="text-sm">Click “Add Expense” to get started.</p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[820px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3">Case</th>
                        <th className="px-4 py-3">Client</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Billable</th>
                        <th className="px-4 py-3">Date</th>
                        {!isClient && <th className="px-4 py-3 text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pageExpenses.map((e, idx) => (
                        <tr key={`${e.caseId || "gen"}-${e.id || idx}`} className="transition-colors hover:bg-slate-50">
                          <td className="px-4 py-3 align-top">
                            <div className="max-w-[220px] truncate font-medium text-slate-800" title={e.caseName}>
                              {e.caseName || "—"}
                            </div>
                            {e.caseNo && <div className="text-xs text-slate-500">{e.caseNo}</div>}
                          </td>
                          <td className="px-4 py-3 align-top text-slate-700">{e.contactName || "—"}</td>
                          <td className="px-4 py-3 align-top text-slate-700">{e.type || "—"}</td>
                          <td className="px-4 py-3 align-top text-right font-semibold text-slate-900">
                            {formatAmount(e.amount)}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                                e.billable
                                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                  : "bg-slate-100 text-slate-600 ring-slate-200"
                              }`}
                            >
                              {e.billable ? "Billable" : "Non-Billable"}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-top whitespace-nowrap text-slate-600">
                            {fmtExpDate(e.date)}
                          </td>
                          {!isClient && (
                            <td className="px-4 py-3 align-top text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleOpenEditModal(e)}
                                  title="Edit"
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteExpense(e)}
                                  title="Delete"
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <Pagination
                currentPage={currentPage}
                totalItems={visibleExpenses.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
              />
            </>
          )}
        </div>
      </main>

      {/* Add/Edit Expense Modal */}
      <AddExpenseForm
        isOpen={isFormOpen}
        onClose={handleCloseModal}
        onAddExpense={handleAddExpense}
        editMode={editMode}
        expenseToEdit={expenseToEdit}
        onEditExpense={handleEditExpense}
        isGlobalView={true}
        cases={cases}
        clients={clients}
        isClient={isClient}
      />
    </div>
  );
}

export default AllExpenses;
